import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  getListApiKeysQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Copy, Trash } from "lucide-react";

export default function ApiKeys() {
  const { data: keys, isLoading } = useListApiKeys();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [label, setLabel] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [revealed, setRevealed] = useState<{ key: string; label: string } | null>(null);

  const create = useCreateApiKey({
    mutation: {
      onSuccess: (res) => {
        setRevealed({ key: res.plaintextKey, label: res.apiKey.label });
        setShowCreate(false);
        setLabel("");
        void queryClient.invalidateQueries({ queryKey: getListApiKeysQueryKey() });
      },
      onError: () => toast({ title: "Failed to create key", variant: "destructive" }),
    },
  });

  const revoke = useRevokeApiKey({
    mutation: {
      onSuccess: () => {
        toast({ title: "Key revoked" });
        void queryClient.invalidateQueries({ queryKey: getListApiKeysQueryKey() });
      },
    },
  });

  const copy = (text: string) => {
    void navigator.clipboard.writeText(text);
    toast({ title: "Copied to clipboard" });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Public API Keys</h1>
          <p className="text-sm text-muted-foreground mt-1">Use these keys to ingest leads from your landing pages, ad campaigns, or external CRMs. See <code className="text-xs bg-muted px-1 rounded">docs/public-api.md</code> for endpoints and examples.</p>
        </div>
        <Button onClick={() => setShowCreate(true)}><Plus className="mr-2 h-4 w-4" />Create Key</Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Your Keys</CardTitle></CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4"><Skeleton className="h-32 w-full" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Prefix</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keys?.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No keys yet.</TableCell></TableRow>}
                {keys?.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell className="font-medium">{k.label}</TableCell>
                    <TableCell className="font-mono text-xs">{k.prefix}…</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{new Date(k.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : "Never"}</TableCell>
                    <TableCell>
                      {k.revokedAt
                        ? <Badge variant="outline" className="bg-red-100 text-red-800 border-transparent">Revoked</Badge>
                        : <Badge variant="outline" className="bg-green-100 text-green-800 border-transparent">Active</Badge>}
                    </TableCell>
                    <TableCell>
                      {!k.revokedAt && (
                        <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Revoke ${k.label}?`)) revoke.mutate({ id: k.id }); }}>
                          <Trash className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create API Key</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Label</Label>
              <Input placeholder="e.g. Landing Page Form" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <p className="text-xs text-muted-foreground">The plaintext key is shown once and never stored.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={() => create.mutate({ data: { label } })} disabled={!label || create.isPending}>{create.isPending ? "Creating..." : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>API Key Created: {revealed?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Copy this key now — it will not be shown again.</p>
            <div className="flex gap-2">
              <Input readOnly value={revealed?.key ?? ""} className="font-mono text-xs" />
              <Button variant="outline" onClick={() => revealed && copy(revealed.key)}><Copy className="h-4 w-4" /></Button>
            </div>
            <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">{`curl -X POST $API/api/public/leads \\
  -H "Authorization: Bearer ${revealed?.key ?? "..."}" \\
  -H "Content-Type: application/json" \\
  -d '{ "customerName": "...", "mobileNumber": "...", "source": "organic_website", "spaceType": "private_cabin", "seatRangeMin": 1, "seatRangeMax": 5, "urgency": "high", "location": "...", "spaceId": 1 }'`}</pre>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealed(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
